import React, { useEffect, useMemo, useState } from 'react';
import { Image, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { sendSingleRequest } from '../../apis';
import {
  BreadcrumbComponent,
  Button,
  CaretDownIcon,
  Dropdown,
  Input,
  ModalComponent,
  Spinner,
} from '../../components/shared';
import { useWidth } from '../../lib/useWidth';
import useMenuHandler from '../../lib/useMenuHandler';
import right_icon from '../../images/right-icon.png';
import getBreadcrumbMenu from '../../lib/BreadcrumbMenu';
import { useDispatch, useSelector } from 'react-redux';
import { loadSpatStorages } from '../../store/farms/farms.actions';
import SpatStorageMobileTable from './SpatStorageMobileTable';
import {
  IFarmResource,
  ILineResource,
  ISpatSeed,
  ISpatSource,
  ISpatStorageResource,
} from '../../entities/farms.entities';
import { formatNumber } from '../../entities/util-functions';
import SpatStorageModal from './SpatStorageModal';
import SpatJourneyModal from '../../components/view-modals/SpatJourneyModal';
import {
  selectFarmsData,
  selectSpatStorages,
} from '../../store/farms/farms.selector';
import { TBusinessType } from '../../entities/general.entities';
import { useLocation } from 'react-router-dom';
import { TLang } from '../../entities/ui.entities';
import { translate } from '../../lib/lang.helper';
import { selectLang } from '../../store/ui/ui.selector';
import {
  pcsToDoz,
  defaultDateFormat,
  displayValueUnit,
} from '../../lib/common.helpers';
import DotsMenu from '../../components/shared/dropdown-menu/DotsMenu';
import FlupsyModal from './FlupsyModal';
import { HarvestSizeRange, StageLabelSpan } from '../../components/CommonViews';
import BulkSeedingModal from '../../components/farm-modals/BulkSeedingModal';
import SpatArchiveModal from './SpatArchiveModal';
import SpatsMergeModal from './SpatsMergeModal';
import { selectAccount } from '../../store/auth/auth.selector';
import { CROP_COLORS } from '../../lib/constant';

const spatLabel = (
  type: TBusinessType,
  amount: number | null,
  unitType: any,
) => {
  if (amount === null) {
    return '-';
  }
  if (type === 'MUSSEL') {
    return displayValueUnit(amount, unitType, 'weight_kg');
  }
  if (type === 'OYSTER') {
    return `${amount} pcs (${pcsToDoz(amount)} dz)`;
  }
  return formatNumber(amount);
};

const columns = (lang: TLang | undefined, unitType: any) => [
  {
    title: 'ID',
    key: 'id',
    render: (x: ISpatStorageResource) => (
      <div
        style={{
          textAlign: 'center',
          borderBottom: `5px solid ${CROP_COLORS[x.type]}`,
        }}
      >
        {x.id}
      </div>
    ),
  },
  {
    title: translate(lang, 'Source'),
    key: 'source',
    render: (x: ISpatStorageResource) => <span>{x.source}</span>,
  },
  {
    title: translate(lang, 'Seed type'),
    key: 'seed_type',
    render: (x: ISpatStorageResource) => <span>{x.seed_type ?? '-'}</span>,
  },
  {
    title: translate(lang, 'Collection date'),
    key: 'collection_date',
    render: (x: ISpatStorageResource) => (
      <span>{defaultDateFormat(x.collection_date)}</span>
    ),
  },
  {
    title: translate(lang, 'Seeded date'),
    key: 'seeded_date',
    render: (x: ISpatStorageResource) => (
      <span>{defaultDateFormat(x.seeded_date)}</span>
    ),
  },
  {
    title: translate(lang, 'Condition'),
    key: 'condition',
    render: (x: ISpatStorageResource) => <span>{x.condition}</span>,
  },
  {
    title: translate(lang, 'Amount'),
    key: 'weight',
    render: (x: ISpatStorageResource) => (
      <div style={{ whiteSpace: 'nowrap' }}>
        <div>
          {`${translate(lang, 'Total')}: ${spatLabel(x.type, x.weight, unitType)}`}
        </div>
        <div>
          {`${translate(lang, 'Available')}: ${spatLabel(x.type, x.available_weight, unitType)}`}
        </div>
        <div>
          {`${translate(lang, 'Harvested')}: ${spatLabel(x.type, x.harvested_weight, unitType)}`}
        </div>
        <div>
          {`${translate(lang, 'Seeded')}: ${formatNumber(
            x.seedings?.reduce((p, c) => p + c.spat_amount, 0),
          )}`}
        </div>
      </div>
    ),
  },
  {
    title: translate(lang, 'Size'),
    key: 'size',
    render: (x: ISpatStorageResource) => (
      <span>
        <HarvestSizeRange data={x.harvest ?? undefined} />
      </span>
    ),
  },
  {
    title: translate(lang, 'Stage'),
    key: 'stage',
    render: (x: ISpatStorageResource) => <StageLabelSpan stage={x.stage} />,
  },
];

const returnedColumns = (lang: TLang | undefined, unitType: any) => [
  {
    title: 'ID',
    key: 'id',
    render: (x: ISpatStorageResource) => <span>{x.id}</span>,
  },
  {
    title: translate(lang, 'Source'),
    key: 'source',
    render: (x: ISpatStorageResource) => <span>{x.source}</span>,
  },
  {
    title: translate(lang, 'Seed type'),
    key: 'seed_type',
    render: (x: ISpatStorageResource) => <span>{x.seed_type ?? '-'}</span>,
  },
  {
    title: translate(lang, 'Collection date'),
    key: 'collection_date',
    render: (x: ISpatStorageResource) => (
      <span>{defaultDateFormat(x.collection_date)}</span>
    ),
  },
  {
    title: translate(lang, 'Condition'),
    key: 'condition',
    render: (x: ISpatStorageResource) => <span>{x.condition}</span>,
  },
  {
    title: translate(lang, 'Amount'),
    key: 'weight',
    render: (x: ISpatStorageResource) => (
      <div style={{ whiteSpace: 'nowrap' }}>
        {spatLabel(x.type, x.weight, unitType)}
      </div>
    ),
  },
  {
    title: translate(lang, 'Size'),
    key: 'size',
    render: (x: ISpatStorageResource) => (
      <span>
        <HarvestSizeRange data={x.harvest ?? undefined} />
      </span>
    ),
  },
  {
    title: translate(lang, 'Stage'),
    key: 'stage',
    render: (x: ISpatStorageResource) => <StageLabelSpan stage={x.stage} />,
    align: 'left',
  },
];

const seedColumns = (type: TBusinessType, lang: TLang | undefined) => [
  {
    title: translate(lang, 'Farm Name'),
    key: 'farm',
    render: (x: ISpatSeed) => <span>{x.line?.farm.name ?? ''}</span>,
  },
  {
    title: translate(lang, 'Line Name'),
    key: 'line',
    render: (x: ISpatSeed) => <span>{x.line?.line_name ?? ''}</span>,
  },
  {
    title: translate(lang, 'Season'),
    key: 'season',
    render: (x: ISpatSeed) => <span>{x.season_name}</span>,
  },
  {
    title:
      type === 'MUSSEL'
        ? `${translate(lang, 'Line Length')} (m)`
        : type === 'OYSTER'
          ? translate(lang, 'Basket count')
          : `${translate(lang, 'Tank Area')} (m²)`,
    key: 'line_length',
    render: (x: ISpatSeed) => (
      <span>
        {type === 'MUSSEL'
          ? x.line_length
          : type === 'OYSTER'
            ? x.basket_count
            : x.tank_area}
      </span>
    ),
  },
  {
    title: translate(lang, 'Amount'),
    key: 'amount',
    render: (x: ISpatSeed) => <span>{formatNumber(x.spat_amount)}</span>,
  },
  {
    title: translate(lang, 'Date seeded'),
    key: 'planned_date_seed',
    render: (x: ISpatSeed) => (
      <span>{defaultDateFormat(x.planned_date_seed)}</span>
    ),
  },
];

const SpatTrackItem: React.FC<{ spat: ISpatSource; c: boolean }> = ({
  spat,
  c,
}) => {
  const spatStorages = useSelector(selectSpatStorages);
  const curStorage = spatStorages.find(x => x.id === spat.id);

  return (
    <>
      <div className='mr-17 ml-17 white-card'>
        <span>Name: {spat.source}</span>
        <br />
        <span>Weight: {curStorage?.weight}</span>
        <br />
        {curStorage?.source_line && (
          <span>
            {`Harvested from ${curStorage.source_line.farm.name} - ${curStorage.source_line.line_name}`}
          </span>
        )}
      </div>
      {c && (
        <div className='d-flex align-center justify-center'>
          <Image src={right_icon} />
        </div>
      )}
    </>
  );
};

const SpatTrackRow: React.FC<{ spats: ISpatSource[] }> = ({ spats }) => (
  <div className='d-flex justify-between ml-9 mt-9 mb-9'>
    {spats.map((item: any, idx: number) => (
      <SpatTrackItem
        key={idx}
        spat={spats[spats.length - idx - 1]}
        c={idx < spats.length - 1}
      />
    ))}
  </div>
);

const typeOptions = [
  { id: '', value: '', label: 'All' },
  { id: 'MUSSEL', value: 'MUSSEL', label: 'Mussel' },
  { id: 'OYSTER', value: 'OYSTER', label: 'Oyster' },
  { id: 'SEAWEED', value: 'SEAWEED', label: 'Seaweed' },
];

const SpatStoragesPage = () => {
  const width = useWidth();
  const dispatch = useDispatch();
  const query = new URLSearchParams(useLocation().search);
  const spatStorages = useSelector(selectSpatStorages);
  const lang = useSelector(selectLang);
  const farms = useSelector(selectFarmsData);
  const unitType = useSelector(selectAccount)?.display_unit;

  const breadcrumbItems = getBreadcrumbMenu('SPAT_STORAGES', lang);
  const { redirectToLine } = useMenuHandler();

  const [showModal, setShowModal] = useState(false);
  const [showFlupsyModal, setShowFlupsyModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ISpatStorageResource>();
  const [deleteShow, setDeleteShow] = useState(false);
  const [disable, setDisable] = useState(false);
  const [fltType, setFltType] = useState<'AVL' | 'ACV' | 'USED' | 'RETURNED'>(
    'AVL',
  );
  const [journeyData, setJourneyData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    pageSize: 7,
  });
  const [busType, setBusType] = useState<TBusinessType | ''>(
    (query.get('type') as TBusinessType | undefined) ?? '',
  );
  const [filterSource, setFilterSource] = useState('');
  const [filterFarm, setFilterFarm] = useState<IFarmResource>();
  const [filterLine, setFilterLine] = useState<ILineResource>();
  const [archiveData, setArchiveData] = useState<ISpatStorageResource>();
  const [seedSpat, setSeedSpat] = useState<ISpatStorageResource>();
  const [visibleMerge, setVisibleMerge] = useState(false);
  const [returnedSpats, setReturnedSpats] = useState<ISpatStorageResource[]>(
    [],
  );

  const onAddClick = () => {
    setSelectedItem(undefined);
    setShowModal(true);
  };
  const showEditModal = (item: ISpatStorageResource) => {
    setShowModal(true);
    setSelectedItem(item);
  };
  const showDeleteModal = (item: ISpatStorageResource) => {
    setSelectedItem(item);
    setDeleteShow(true);
  };

  const onAddFLupsyClick = () => {
    setSelectedItem(undefined);
    setShowFlupsyModal(true);
  };
  const deleteSelectedItem = () => {
    if (!selectedItem) return;
    setDisable(true);
    sendSingleRequest(
      {},
      'DELETE',
      `api/farm/spat-storage/${selectedItem.id}`,
      true,
    ).then(res => {
      setDeleteShow(false);
      setDisable(false);
      dispatch(loadSpatStorages());
    });
  };
  const redirectTo = (dataRow: ISpatSeed) => {
    if (dataRow.line) {
      redirectToLine(dataRow.line?.farm.id, dataRow?.line.id);
    }
  };
  const selectFarm = (id_str: string) => {
    const farm = farms.find(f => f.id === Number(id_str));
    setFilterFarm(farm);
    setFilterLine(undefined);
  };
  const selectLine = (id_str: string) => {
    const line = filterFarm?.lines.find(l => l.id === Number(id_str));
    setFilterLine(line);
  };

  const loadReturnedSpats = async () => {
    setLoading(true);
    try {
      const res = await sendSingleRequest(
        { status: 'RETURNED' },
        'GET',
        'api/farm/spat-storage',
        true,
      );
      if (res.status) {
        setReturnedSpats(res.data);
      } else {
        alert(translate(lang, res.data?.message ?? 'Server error'));
      }
    } catch (error) {
      alert(translate(lang, 'Server error'));
    } finally {
      setLoading(false);
    }
  };
  const tableColumns = useMemo(() => {
    const baseColumns =
      fltType === 'RETURNED'
        ? returnedColumns(lang, unitType)
        : columns(lang, unitType);

    if (fltType === 'RETURNED') {
      return baseColumns as ColumnsType<ISpatStorageResource>;
    }

    return [
      ...baseColumns,
      {
        title: translate(lang, 'More'),
        key: 'more',
        align: 'right',
        render: (x: ISpatStorageResource) => {
          let menus = [
            { label: translate(lang, 'Edit'), onClick: () => showEditModal(x) },
            {
              label: translate(lang, 'Delete'),
              onClick: () => showDeleteModal(x),
            },
            {
              label: translate(lang, 'Archive'),
              onClick: () => setArchiveData(x),
            },
          ];
          if (x.available_weight && x.available_weight > 0) {
            menus.push({
              label: translate(lang, 'Seed on lines'),
              onClick: () => setSeedSpat(x),
            });
          }
          return <DotsMenu items={menus} />;
        },
      },
    ] as ColumnsType<ISpatStorageResource>;
  }, [lang, fltType]);

  const filteredSpats = useMemo(() => {
    let result =
      fltType === 'ACV'
        ? spatStorages.filter(
            x =>
              (x.available_weight ?? 0) <= 0 &&
              (!x.harvest || x.harvest.is_catch_spat === true),
          )
        : fltType === 'AVL'
          ? spatStorages.filter(x => (x.available_weight ?? 0) > 0)
          : fltType === 'USED'
            ? spatStorages.filter(
                x =>
                  (x.available_weight ?? 0) <= 0 &&
                  x.harvest &&
                  !x.harvest.is_catch_spat,
              )
            : fltType === 'RETURNED'
              ? returnedSpats
              : [];
    if (busType.length > 0) {
      result = result.filter(x => x.type === busType);
    }
    if (filterFarm) {
      result = result.filter(x => x.source_line?.farm.id === filterFarm.id);
    }
    if (filterLine) {
      result = result.filter(x => x.source_line?.id === filterLine.id);
    }
    if (filterSource.trim().length > 0) {
      result = result.filter(x =>
        x.source.toLowerCase().includes(filterSource.toLowerCase()),
      );
    }
    return result;
  }, [
    fltType,
    spatStorages,
    returnedSpats,
    busType,
    filterFarm,
    filterLine,
    filterSource,
  ]);

  const openJourney = async (id: number) => {
    const spatStorage = spatStorages.find(x => x.id === id);
    if (!spatStorage) return;

    setLoading(true);
    const res = await sendSingleRequest(
      {},
      'GET',
      `api/farm/spats-journey/${id}`,
      true,
    );
    setLoading(false);

    if (res.status) {
      setJourneyData(res.data);
    } else {
      alert(translate(lang, res.data?.message ?? 'Server error'));
    }
  };

  useEffect(() => {
    dispatch(loadSpatStorages());
  }, [dispatch]);

  useEffect(() => {
    if (fltType === 'RETURNED') {
      loadReturnedSpats();
    }
  }, [fltType]);

  return (
    <div className='h-calc-80 bg-secondary spat-storages-page'>
      {loading && (
        <div className='loader'>
          <Spinner />
        </div>
      )}
      <div className='container pos-relative farms__line'>
        <div className='d-flex justify-between align-center pl-21 pr-15 pt-28 pb-28'>
          <BreadcrumbComponent items={breadcrumbItems} />
          <div className='d-flex'>
            <Button
              color='green'
              size={1}
              width='middle'
              type='fill'
              onClick={onAddFLupsyClick}
            >
              {translate(lang, '_baby_spat_storage_flupsy')}
            </Button>
            <Button
              className='mr-12 ml-12'
              color='blue'
              size={1}
              width='middle'
              type='fill'
              onClick={onAddClick}
            >
              {translate(lang, 'Add Spat Storage')}
            </Button>
            <Button
              color='green'
              size={1}
              width='middle'
              type='fill'
              onClick={() => setVisibleMerge(true)}
            >
              {translate(lang, 'Merge Spat Storages')}
            </Button>
          </div>
        </div>
        <div className='farms__line-content'>
          <div className='farms__main'>
            <div className='mb-17 d-flex align-end justify-between'>
              <div className='d-flex align-end'>
                <div className='mr-12' style={{ width: '150px' }}>
                  <Dropdown
                    label=''
                    options={typeOptions.map(x => ({
                      ...x,
                      label: translate(lang, x.label),
                    }))}
                    value={busType}
                    onChange={v => setBusType(v as TBusinessType)}
                  />
                </div>
                <div className='filter-option mr-12'>
                  <Dropdown
                    label=''
                    options={[
                      {
                        id: 'AVL',
                        value: 'AVL',
                        label: translate(lang, 'View Available'),
                      },
                      {
                        id: 'ACV',
                        value: 'ACV',
                        label: translate(lang, 'View Full Seeded'),
                      },
                      {
                        id: 'USED',
                        value: 'USED',
                        label: translate(lang, 'View Used'),
                      },
                      {
                        id: 'RETURNED',
                        value: 'RETURNED',
                        label: translate(lang, 'View Returned'),
                      },
                    ]}
                    value={fltType}
                    onChange={(v: any) => setFltType(v)}
                  />
                </div>
                <div className='filter-option mr-12'>
                  <Input
                    type='text'
                    label={translate(lang, 'Source')}
                    value={filterSource}
                    onChange={e => setFilterSource(e.target.value)}
                    placeholder={translate(lang, '_search_source_name')}
                  />
                </div>
                <div className='filter-option mr-12'>
                  <Dropdown
                    label={translate(lang, 'Source farm')}
                    options={farms.map(f => ({
                      id: f.id.toString(),
                      value: f.id.toString(),
                      label: f.name,
                    }))}
                    value={filterFarm?.id.toString()}
                    onChange={selectFarm}
                    allowClear={true}
                    showSearch={true}
                  />
                </div>
                {filterFarm && (
                  <div className='filter-option'>
                    <Dropdown
                      label={translate(lang, 'Source line')}
                      options={filterFarm.lines.map(l => ({
                        id: l.id.toString(),
                        value: l.id.toString(),
                        label: l.line_name,
                      }))}
                      value={filterLine?.id.toString()}
                      onChange={selectLine}
                      allowClear={true}
                      showSearch={true}
                    />
                  </div>
                )}
              </div>
            </div>
            <div style={{ overflow: 'auto' }} className='w-100 pos-relative'>
              {width > 768 ? (
                <Table
                  rowKey='id'
                  className='table table--isFarm'
                  style={{
                    width: '1200px',
                    minWidth: '100%',
                    maxWidth: '1200px',
                  }}
                  columns={tableColumns}
                  pagination={pageOptions}
                  onChange={po => setPageOptions(po as any)}
                  dataSource={filteredSpats}
                  expandable={
                    fltType === 'RETURNED'
                      ? undefined
                      : {
                          expandedRowRender: d => (
                            <>
                              <Table
                                rowKey='id'
                                className='table table--isFarm is__child'
                                columns={seedColumns(d.type, lang)}
                                pagination={false}
                                dataSource={d.seedings}
                                onRow={dR => ({
                                  onClick: redirectTo.bind(null, dR),
                                })}
                              />
                              <SpatTrackRow spats={d.spat_parents ?? []} />
                            </>
                          ),
                          expandIcon: ({ onExpand, record }) => (
                            <div
                              className='pt-20 pb-20'
                              onKeyDown={() => undefined}
                              onClick={event => {
                                event.stopPropagation();
                                onExpand(record, event);
                              }}
                            >
                              <CaretDownIcon />
                            </div>
                          ),
                        }
                  }
                  onRow={(r: ISpatStorageResource) => ({
                    onClick:
                      fltType === 'RETURNED'
                        ? undefined
                        : () => openJourney(r.id),
                  })}
                />
              ) : (
                <SpatStorageMobileTable
                  data={filteredSpats}
                  showEditModal={showEditModal}
                  showDeleteModal={showDeleteModal}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {!!showModal && (
        <SpatStorageModal
          visible={true}
          data={selectedItem ?? { type: busType ? busType : undefined }}
          editId={selectedItem?.id}
          onConfirm={() => {
            setShowModal(false);
            setSelectedItem(undefined);
            dispatch(loadSpatStorages());
          }}
          onCancel={() => {
            setSelectedItem(undefined);
            setShowModal(false);
          }}
        />
      )}
      {!!showFlupsyModal && (
        <FlupsyModal
          visible={true}
          onCancel={() => {
            setSelectedItem(undefined);
            setShowFlupsyModal(false);
          }}
          cropType={busType ? busType : undefined}
        />
      )}
      {!!deleteShow && (
        <ModalComponent
          visible={true}
          onCancel={() => setDeleteShow(false)}
          type='delete'
          title={translate(lang, 'Delete')}
          text={translate(lang, '_delete_data')}
          disabled={disable}
          onConfirm={deleteSelectedItem}
        />
      )}
      {!!journeyData && (
        <SpatJourneyModal
          data={journeyData}
          visible={true}
          onClose={() => setJourneyData(undefined)}
        />
      )}
      {!!archiveData && (
        <SpatArchiveModal
          spatStorage={archiveData}
          onClose={() => setArchiveData(undefined)}
        />
      )}
      {!!seedSpat && (
        <BulkSeedingModal
          visible={true}
          batchID={seedSpat.id}
          onClose={() => setSeedSpat(undefined)}
        />
      )}
      {visibleMerge && (
        <SpatsMergeModal
          visible={visibleMerge}
          onClose={() => setVisibleMerge(false)}
          type={busType ? busType : undefined}
        />
      )}
    </div>
  );
};

export default SpatStoragesPage;
